/**
 * Page break markup shared by everything that reads, rewrites or prompts about document HTML
 * outside the editor (Inky's edit planners, AI drafting prompts, sanitizers).
 *
 * The editor's `pageBreak` node (src/extensions/PageBreak.js) saves as {@link PAGE_BREAK_HTML}
 * (plus a `data-block-id` once BlockId has stamped it). It may only live at the top level of a
 * document, so prompts tell the model to write it between blocks, never inside a paragraph,
 * list, table or field.
 */

/** Canonical saved HTML of a page break. */
export const PAGE_BREAK_HTML = '<div data-page-break="true" class="page-break"></div>';

/** Attribute that marks a page break element. */
export const PAGE_BREAK_ATTR = 'data-page-break';

/** Opening tag of a page break element (canonical or legacy print CSS). */
const PAGE_BREAK_TAG_RE =
  /<div\b[^>]*(?:\sdata-page-break\b|\sstyle\s*=\s*["'][^"']*(?:page-break-after\s*:\s*always|break-after\s*:\s*page))[^>]*>/i;

/**
 * Whether html contains a page break (the canonical div or a legacy print-CSS div).
 * @param {string} html - Html.
 * @returns {boolean} True when a page break is present.
 */
export const htmlHasPageBreak = (html) => PAGE_BREAK_TAG_RE.test(String(html || ''));

/**
 * Count the page breaks in html.
 * @param {string} html - Html.
 * @returns {number} Number of page break elements.
 */
export const countPageBreaks = (html) =>
  (String(html || '').match(new RegExp(PAGE_BREAK_TAG_RE.source, 'gi')) || []).length;

/**
 * One prompt rule, phrased for AI planners and drafters, that keeps page breaks in place and
 * says how to add one.
 * @param {{canInsert?: boolean}} [options] - canInsert: the model may add a page break when asked.
 * @returns {string} Rule text (no leading bullet).
 */
export const pageBreakPromptRule = ({ canInsert = true } = {}) =>
  `PAGE BREAKS: ${PAGE_BREAK_HTML} is a page break (the next content starts on a new page in Pages view, PDF and Word). ` +
  'Keep every page break exactly where it is, even when you rewrite the sections around it; remove one only when the user asks.' +
  (canInsert
    ? ` When the user asks for a page break (or for a section to start on a new page), add exactly ${PAGE_BREAK_HTML} as its own top-level block between two blocks, never inside a paragraph, heading, list, table or field.`
    : '');
